/**
 * Piecewise cubic hermite evaluation over CurveDef.
 * This is the CPU reference implementation; the GPU samples the same curve
 * baked into a texture (see bake.ts), and the two are unit-tested against
 * each other within tolerance.
 */
/** Returns points sorted by t (non-mutating). Callers may pass unsorted editor state. */
export function sortedPoints(def) {
    return [...def.points].sort((a, b) => a.t - b.t);
}
/** Auto tangent (Catmull-Rom style slope) for point i of a sorted point list. */
function autoTangent(pts, i) {
    const prev = pts[Math.max(0, i - 1)];
    const next = pts[Math.min(pts.length - 1, i + 1)];
    const dt = next.t - prev.t;
    if (dt <= 1e-8)
        return 0;
    return (next.v - prev.v) / dt;
}
export function evalCurve(def, t) {
    const pts = sortedPoints(def);
    if (pts.length === 0)
        return 0;
    if (pts.length === 1)
        return pts[0].v;
    if (t <= pts[0].t)
        return pts[0].v;
    if (t >= pts[pts.length - 1].t)
        return pts[pts.length - 1].v;
    let i = 0;
    while (i < pts.length - 2 && t >= pts[i + 1].t)
        i++;
    const p0 = pts[i];
    const p1 = pts[i + 1];
    const span = p1.t - p0.t;
    if (span <= 1e-8)
        return p1.v;
    const m0 = p0.outTan ?? autoTangent(pts, i);
    const m1 = p1.inTan ?? autoTangent(pts, i + 1);
    const s = (t - p0.t) / span;
    const s2 = s * s;
    const s3 = s2 * s;
    const h00 = 2 * s3 - 3 * s2 + 1;
    const h10 = s3 - 2 * s2 + s;
    const h01 = -2 * s3 + 3 * s2;
    const h11 = s3 - s2;
    return h00 * p0.v + h10 * span * m0 + h01 * p1.v + h11 * span * m1;
}
/** Min/max of the curve sampled densely — used by editors for auto-ranging. */
export function curveRange(def, samples = 64) {
    let min = Infinity;
    let max = -Infinity;
    for (let i = 0; i <= samples; i++) {
        const v = evalCurve(def, i / samples);
        if (v < min)
            min = v;
        if (v > max)
            max = v;
    }
    if (!isFinite(min))
        return [0, 1];
    return [min, max];
}
// ---------------------------------------------------------------------------
// Presets (plan §1.1: linear, easeOut, easeOutBack/overshoot, pulse, flicker)
// ---------------------------------------------------------------------------
function flickerPoints() {
    // Deterministic sawtooth-ish flicker; sharp tangents make it strobe rather than wobble.
    const levels = [1, 0.35, 0.9, 0.15, 0.75, 0.45, 1, 0.25, 0.85, 0.1, 0.65, 0];
    return levels.map((v, i) => ({ t: i / (levels.length - 1), v, inTan: 0, outTan: 0 }));
}
const PRESET_FACTORIES = {
    linear: () => [
        { t: 0, v: 0 },
        { t: 1, v: 1 },
    ],
    one: () => [
        { t: 0, v: 1 },
        { t: 1, v: 1 },
    ],
    easeIn: () => [
        { t: 0, v: 0, outTan: 0 },
        { t: 1, v: 1, inTan: 2.5 },
    ],
    easeOut: () => [
        { t: 0, v: 0, outTan: 2.5 },
        { t: 1, v: 1, inTan: 0 },
    ],
    easeInOut: () => [
        { t: 0, v: 0, outTan: 0 },
        { t: 1, v: 1, inTan: 0 },
    ],
    // The signature squash/stretch curve: 0 → 130% → settle at 100%.
    easeOutBack: () => [
        { t: 0, v: 0, outTan: 6 },
        { t: 0.35, v: 1.3, inTan: 0, outTan: 0 },
        { t: 1, v: 1, inTan: 0 },
    ],
    pulse: () => [
        { t: 0, v: 0, outTan: 8 },
        { t: 0.15, v: 1, inTan: 0, outTan: 0 },
        { t: 1, v: 0, inTan: -0.5 },
    ],
    fadeInOut: () => [
        { t: 0, v: 0 },
        { t: 0.2, v: 1, inTan: 0, outTan: 0 },
        { t: 0.75, v: 1, inTan: 0, outTan: 0 },
        { t: 1, v: 0 },
    ],
    flicker: flickerPoints,
};
export const CURVE_PRESET_IDS = Object.keys(PRESET_FACTORIES);
export function curvePreset(id) {
    return { preset: id, points: PRESET_FACTORIES[id]() };
}
